import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getContractors } from 'services/contractors';
import { updatePayroll, previewPayroll } from 'services/payrolls';

const ContractorPaymentsModal = ({ payroll, onClose }) => {
    const queryClient = useQueryClient();
    const [items, setItems] = useState({});
    const initializedRef = useRef(false);

    const { data: contractors = [], isLoading } = useQuery({
        queryKey: ['contractors'],
        queryFn: getContractors,
    });

    const { data: previewData } = useQuery({
        queryKey: ['payroll-preview', payroll.id],
        queryFn: () => previewPayroll(payroll.id),
        staleTime: 0,
    });

    useEffect(() => {
        if (!initializedRef.current && previewData?.contractors?.length) {
            initializedRef.current = true;
            const existing = {};
            previewData.contractors.forEach(c => {
                existing[c.contractorId] = {
                    hours: c.hours ?? '',
                    payRate: c.payRate ?? '',
                    reimbursement: c.reimbursement ?? '',
                    paymentMethod: c.paymentMethod ?? 'Check',
                };
            });
            setItems(existing);
        }
    }, [previewData]);

    const { mutate: save, isPending, isError, error } = useMutation({
        mutationFn: (contractorItems) => updatePayroll({
            payrollId: payroll.id,
            contractorItems,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payrolls'] });
            queryClient.invalidateQueries({ queryKey: ['payroll-preview', payroll.id] });
            onClose();
        },
    });

    const handleToggle = (id) => {
        setItems(prev => {
            const next = { ...prev };
            if (next[id]) {
                delete next[id];
            } else {
                const contractor = contractors.find(c => c.id === id);
                next[id] = {
                    hours: '',
                    payRate: contractor?.payRate ?? '',
                    reimbursement: '',
                    paymentMethod: contractor?.defaultPaymentMethod === 'DirectDeposit' ? 'DirectDeposit' : 'Check',
                };
            }
            return next;
        });
    };

    const handleChange = (id, field, value) => {
        setItems(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value },
        }));
    };

    const periodDays = useMemo(() => {
        if (!payroll.startDate || !payroll.endDate) return 14;
        return Math.max(1, (new Date(payroll.endDate) - new Date(payroll.startDate)) / (1000 * 60 * 60 * 24) + 1);
    }, [payroll.startDate, payroll.endDate]);

    const calcGrossPay = (payType, hours, payRate) => {
        switch (payType) {
            case 'Hourly':  return hours * payRate;
            case 'Weekly':  return payRate * (periodDays / 7);
            case 'Monthly': return payRate * (periodDays / 30);
            default:        return payRate; // FlatFee
        }
    };

    const handleSave = () => {
        const contractorItems = Object.entries(items).map(([contractorId, v]) => {
            const contractor = contractors.find(c => c.id === contractorId);
            const payType = contractor?.payType || 'Hourly';
            const isHourly = payType === 'Hourly';
            const hours = parseFloat(v.hours) || 0;
            const payRate = parseFloat(v.payRate) || 0;
            const grossPay = calcGrossPay(payType, hours, payRate);
            return {
                contractorId,
                paymentMethod: v.paymentMethod || 'Check',
                hours: isHourly ? hours : 0,
                payRate,
                grossPay,
                reimbursement: parseFloat(v.reimbursement) || 0,
            };
        });
        save(contractorItems);
    };

    const hasAny = Object.keys(items).length > 0;
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1060, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 860, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>

                {/* Header */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h5 className='mb-0 fw-semibold'>Add / Update Contractor Payments</h5>
                        <p className='mb-0 mt-1' style={{ fontSize: 13, color: '#888' }}>
                            Pay Period: {formatDate(payroll.startDate)} – {formatDate(payroll.endDate)}
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: '24px' }}>
                    {isLoading ? (
                        <div className='text-center py-4'>Loading contractors...</div>
                    ) : contractors.length === 0 ? (
                        <div className='text-center py-4' style={{ color: '#888' }}>No contractors found. Add contractors first.</div>
                    ) : (
                        <>
                            <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
                                Check the contractors to include in this payroll and enter their payment details. Previously saved entries are pre-filled.
                            </p>
                            <div style={{ overflowX: 'auto' }}>
                                <table className='table table-sm' style={{ fontSize: 13, minWidth: 700 }}>
                                    <thead style={{ background: '#f8f9fa' }}>
                                        <tr>
                                            <th style={{ width: 40 }}></th>
                                            <th>Contractor</th>
                                            <th style={{ width: 80 }}>Pay Type</th>
                                            <th style={{ width: 90 }}>Hours</th>
                                            <th style={{ width: 110 }}>Pay Rate ($)</th>
                                            <th style={{ width: 110 }}>Gross Pay ($)</th>
                                            <th style={{ width: 120 }}>Reimbursement ($)</th>
                                            <th style={{ width: 150 }}>Payment Method</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contractors.filter(c => c.active !== false && c.onboardingStatus !== 'pending_review').map(c => {
                                            const included = !!items[c.id];
                                            const item = items[c.id] || {};
                                            const payType = c.payType || 'Hourly';
                                            const isHourly = payType === 'Hourly';
                                            const grossPay = calcGrossPay(
                                                payType,
                                                parseFloat(item.hours) || 0,
                                                parseFloat(item.payRate) || 0
                                            ).toFixed(2);
                                            return (
                                                <tr key={c.id} style={{ background: included ? '#fff8f0' : '', transition: 'background 0.2s' }}>
                                                    <td>
                                                        <input
                                                            type='checkbox'
                                                            checked={included}
                                                            onChange={() => handleToggle(c.id)}
                                                            style={{ cursor: 'pointer', width: 16, height: 16 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className='fw-medium'>{c.firstName} {c.lastName}</span>
                                                        {c.businessName && <span style={{ color: '#888', marginLeft: 6, fontSize: 12 }}>({c.businessName})</span>}
                                                    </td>
                                                    <td>
                                                        <span className='badge' style={{
                                                            background: payType === 'Hourly' ? '#e3f2fd' : payType === 'FlatFee' ? '#e8f5e9' : '#fce4ec',
                                                            color: payType === 'Hourly' ? '#1565c0' : payType === 'FlatFee' ? '#2e7d32' : '#880e4f',
                                                            fontWeight: 500, fontSize: 11, padding: '3px 7px', borderRadius: 4
                                                        }}>
                                                            {payType === 'FlatFee' ? 'Flat Fee' : payType}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {isHourly ? (
                                                            <input
                                                                type='number'
                                                                className='form-control form-control-sm'
                                                                placeholder='0'
                                                                min='0'
                                                                disabled={!included}
                                                                value={item.hours ?? ''}
                                                                onChange={e => handleChange(c.id, 'hours', e.target.value)}
                                                            />
                                                        ) : (
                                                            <span style={{ color: '#aaa', fontSize: 12 }}>N/A</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type='number'
                                                            className='form-control form-control-sm'
                                                            placeholder='0.00'
                                                            min='0'
                                                            step='0.01'
                                                            disabled={!included}
                                                            value={item.payRate ?? ''}
                                                            onChange={e => handleChange(c.id, 'payRate', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type='text'
                                                            className='form-control form-control-sm'
                                                            readOnly
                                                            value={included ? grossPay : ''}
                                                            style={{ background: '#f0f9f0', color: '#155724', fontWeight: 600, cursor: 'default' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type='number'
                                                            className='form-control form-control-sm'
                                                            placeholder='0.00'
                                                            min='0'
                                                            step='0.01'
                                                            disabled={!included}
                                                            value={item.reimbursement ?? ''}
                                                            onChange={e => handleChange(c.id, 'reimbursement', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <select
                                                            className='form-select form-select-sm'
                                                            disabled={!included}
                                                            value={item.paymentMethod ?? (c.defaultPaymentMethod === 'DirectDeposit' ? 'DirectDeposit' : 'Check')}
                                                            onChange={e => handleChange(c.id, 'paymentMethod', e.target.value)}
                                                        >
                                                            <option value='Check'>Check</option>
                                                            <option value='DirectDeposit'>Direct Deposit</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {isError && (
                                <p className='text-danger mt-2' style={{ fontSize: 13 }}>
                                    Failed to save contractor payments. {error?.message}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button className='cancel-button px-4' onClick={onClose}>Cancel</button>
                    <button
                        className='button-orange px-5'
                        onClick={handleSave}
                        disabled={isPending}
                    >
                        {isPending ? 'Saving...' : 'Save Contractor Payments'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContractorPaymentsModal;

